import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { DefaultElementProps } from 'common/interfaces'
import { SizingContainer } from 'components/SizingContainer'
import TextFieldOffline from 'components/TextFieldOffline'
import MapAutocompleteField, {
  MapAutocompleteFieldProps,
} from 'elements/MapAddress/components/MapAutocompleteField'
import { MapAddressState } from 'elements/MapAddress/stateInterface'
import { useBoolean, useIsOnline } from 'utils/hook'
import { validationsToMapRules } from 'utils/validationsToRules'

import { MapAddressAutocomplete as MapAddressAutocompleteProps } from './interface'

export default function MapAddressAutocomplete({
  name,
  validations,
  label,
  placeholder = 'Street, City, Address, Zip',
  onFocus,
  onChange,
}: MapAddressAutocompleteProps & DefaultElementProps): JSX.Element {
  const isOnline = useIsOnline()

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: {},
    rules: validationsToMapRules(validations),
  })

  if (!isOnline) {
    return <TextFieldOffline label={label} field={field} />
  }

  const inputRef = useRef(null)
  const mapRef: MutableRefObject<google.maps.Map | null> = useRef(null)

  const [inputValue, setInputValue] = useState<MapAddressState>(field.value)

  const [isLoaded, setLoaded] = useBoolean(false)

  const initMap = (): google.maps.places.Autocomplete | undefined => {
    if (inputRef.current !== null) {
      setLoaded()

      const mapOptions = {
        center: { lat: 0, lng: 0 },
        zoom: 8,
      }
      mapRef.current = new google.maps.Map(inputRef.current, mapOptions)

      const autocompleteOptions = {
        fields: ['formatted_address', 'geometry', 'name'],
        strictBounds: false,
      }

      return new google.maps.places.Autocomplete(inputRef.current, autocompleteOptions)
    }
  }

  const handleAddressChanged: MapAutocompleteFieldProps['onChange'] = (value) => {
    field.onChange(value)
    onChange?.({ value })
    setInputValue(value)
  }

  useEffect(() => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env
      .GOOGLE_MAPS_API_KEY!}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = initMap
    document.head.appendChild(script)
  }, [])

  return (
    <SizingContainer className="map-address-auto-wrap">
      {isLoaded && (
        <MapAutocompleteField
          label={label}
          value={inputValue.formattedAddress ?? ''}
          onChange={handleAddressChanged}
          onFocus={onFocus}
          error={error}
          placeholder={placeholder}
          map={mapRef.current!}
          required={validations?.required?.value}
          sx={{
            width: '100%',
          }}
        />
      )}
      <input ref={inputRef} type="hidden" />
    </SizingContainer>
  )
}
